import { io, Socket } from 'socket.io-client';
import config from 'src/config';
import {
  populateRedirectDomainsStoreCacheApiRequest,
  populateTrackingDomainsStoreCacheApiRequest,
} from '@/services/api/api-services/domains';
import { populateIntegrationsStoreCacheApiRequest } from '@/services/api/api-services/integrations';
import { populateAffiliateNetworksStoreCacheApiRequest } from '@/services/api/api-services/affiliate-networks';
import { populateCampaignsStoreCacheApiRequest } from '@/services/api/api-services/campaigns';
import { populateFlowsStoreCacheApiRequest } from '@/services/api/api-services/flows';
import { populateLandingsStoreCacheApiRequest } from '@/services/api/api-services/landings';
import { populateOffersStoreCacheApiRequest } from '@/services/api/api-services/offers';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { populateTrafficSourcesStoreCacheApiRequest } from '@/services/api/api-services/traffic-sources';
import {
  getBillingAddonsApiRequest,
  getBillingCustomerApiRequest,
  getBillingSubscriptionApiRequest,
} from '@/services/api/billing-api';
import { getColumnSettingsApiRequest } from '@/services/api/column-settings-api';
import { useStore } from '@/store';
import { debounce } from 'lodash';

let socket: Socket = null;

const connectWebsocket = () => {
  const store = useStore();
  socket = io(config.api.baseURL, {
    query: {
      accessToken: localStorage.getItem('auth-token'),
    },
    transports: ['websocket'],
  });

  socket.on('http-cache-version', (version: string) => {
    if (import.meta.env.VUE_APP_CONFIG_NAME !== 'production') {
      console.log('[Socket] `http-cache-version` received', version);
    }
    setCacheVersion(version, { broadcast: false });
  });

  socket.on('subscription-updated', (data: { timestamp: Date; userId: string }) => {
    if (import.meta.env.VUE_APP_CONFIG_NAME !== 'production') {
      console.log('[Socket] `subscription-updated` received', data);
    }

    getBillingSubscriptionApiRequest().then((subscription) => {
      store.setBilling(subscription);
    });

    if (!store.user.isMember) {
      getBillingCustomerApiRequest().then((customer) => {
        store.setBillingCustomer(customer);
      });
      getBillingAddonsApiRequest().then((addons) => {
        const addonsById = addons.reduce<Record<string, unknown>>((accumulator, addon) => {
          const addonId = (addon as { id?: string }).id;
          if (addonId) {
            accumulator[addonId] = addon;
          }
          return accumulator;
        }, {});
        store.setBillingAddons(addonsById);
      });
    }

    getColumnSettingsApiRequest().then((res) => store.setSavedColumns(res));
  });

  socket.on('campaigns-updated', (data: { timestamp: Date; userId: string; eventData: { tagsChanged: boolean } }) => {
    populateCampaignsStoreCacheApiRequest();
    if (data?.eventData?.tagsChanged) {
      store.cache.cacheStartUpdating({ cacheProperty: 'tags', cachePropertyLevel2: 'campaigns' });
      populateTagsStoreCacheApiRequest({ type: 'campaigns' });
    }
  });

  socket.on('offers-updated', (data: { timestamp: Date; userId: string; eventData: { tagsChanged: boolean } }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'offers' });
    populateOffersStoreCacheApiRequest();
    if (data?.eventData?.tagsChanged) {
      store.cache.cacheStartUpdating({ cacheProperty: 'tags', cachePropertyLevel2: 'offers' });
      populateTagsStoreCacheApiRequest({ type: 'offers' });
    }
  });

  socket.on('landings-updated', (data: { timestamp: Date; userId: string; eventData: { tagsChanged: boolean } }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'landings' });
    populateLandingsStoreCacheApiRequest();
    if (data?.eventData?.tagsChanged) {
      store.cache.cacheStartUpdating({ cacheProperty: 'tags', cachePropertyLevel2: 'landings' });
      populateTagsStoreCacheApiRequest({ type: 'landings' });
    }
  });

  socket.on('flows-updated', (data: { timestamp: Date; userId: string; eventData: { tagsChanged: boolean } }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'flows' });
    populateFlowsStoreCacheApiRequest();
    if (data?.eventData?.tagsChanged) {
      store.cache.cacheStartUpdating({ cacheProperty: 'tags', cachePropertyLevel2: 'flows' });
      populateTagsStoreCacheApiRequest({ type: 'flows' });
    }
  });

  socket.on('traffic-sources-updated', (data: { timestamp: Date; userId: string; eventData: { tagsChanged: boolean } }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'trafficSources' });
    populateTrafficSourcesStoreCacheApiRequest();
    if (data?.eventData?.tagsChanged) {
      store.cache.cacheStartUpdating({ cacheProperty: 'tags', cachePropertyLevel2: 'traffic-sources' });
      populateTagsStoreCacheApiRequest({ type: 'traffic-sources' });
    }
  });

  socket.on('affiliate-networks-updated', (data: { timestamp: Date; userId: string; eventData: { tagsChanged: boolean } }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'affiliateNetworks' });
    populateAffiliateNetworksStoreCacheApiRequest();
    if (data?.eventData?.tagsChanged) {
      store.cache.cacheStartUpdating({ cacheProperty: 'tags', cachePropertyLevel2: 'affiliate-networks' });
      populateTagsStoreCacheApiRequest({ type: 'affiliate-networks' });
    }
  });

  const debouncedTrackingDomainsPopulateStoreCache = debounce(() => {
    store.cache.cacheStartUpdating({ cacheProperty: 'trackingDomains' });
    populateTrackingDomainsStoreCacheApiRequest();
  }, 500);
  socket.on('tracking-domains-updated', (data: { timestamp: Date; userId: string }) =>
    debouncedTrackingDomainsPopulateStoreCache(),
  );

  socket.on('redirect-domains-updated', (data: { timestamp: Date; userId: string }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'redirectDomains' });
    populateRedirectDomainsStoreCacheApiRequest();
  });

  socket.on('integrations-updated', (data: { timestamp: Date; userId: string }) => {
    store.cache.cacheStartUpdating({ cacheProperty: 'integrations' });
    populateIntegrationsStoreCacheApiRequest();
  });
};

export const setupWebsocket = () => {
  if (!socket) {
    connectWebsocket();
  }
};

export const reConnectWebsocket = () => {
  if (socket?.connected) {
    socket.disconnect();
  }
  connectWebsocket();
};

export const disconnectWebsocket = () => {
  if (socket?.connected) {
    socket.disconnect();
  }
  socket = null;
};

export const setCacheVersion = (version: string, { broadcast } = { broadcast: true }) => {
  localStorage.setItem('http-cache-version', version);
  if (broadcast && socket && socket.connected) {
    socket.emit('http-cache-version', version);
  }
  return version;
};

export const forceCleanCache = () => {
  setCacheVersion(`${Date.now()}`);
};
